import { useMemo, useState } from "react"
import { XMarkIcon } from "@heroicons/react/24/outline"
import {
  useGetAdSetsQuery,
  useGetCampaignsQuery,
} from "~/ui-rtk/api/paidMediaApi"
import type { Campaign } from "~/ui-rtk/api/types/paid-media.types"
import Dialog from "~/ui-rtk/components/ui/common/Dialog"
import MetaAdAccountGate from "~/ui-rtk/components/features/meta-ad-account-gate/MetaAdAccountGate"
import { getServerErrorMessage } from "~/ui-rtk/utils/http-utils"
import BudgetEditor from "./BudgetEditor"
import { describeBudget, ownBudget } from "./budget-utils"

const HIDDEN_STATUSES = new Set(["DELETED", "ARCHIVED"])

function StatusBadge({ status }: { status: string }) {
  let colorClass = "bg-gray-700 text-gray-200"
  if (status === "ACTIVE") colorClass = "bg-green-900 text-green-200"
  else if (status.includes("PAUSED")) {
    colorClass = "bg-yellow-900 text-yellow-200"
  }
  return (
    <span
      className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${colorClass}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  )
}

/** Ad set budgets of an ABO campaign, each with its own editor. */
function AdSetBudgets({ campaign }: { campaign: Campaign }) {
  const {
    data: adSets,
    isLoading,
    error,
  } = useGetAdSetsQuery({
    platform: "meta",
    campaignId: campaign.id,
  })

  if (isLoading) {
    return <p className="text-sm text-neutral">Loading ad sets from Meta…</p>
  }
  if (error) {
    return (
      <p className="text-sm text-red-400">
        {getServerErrorMessage(error, "Failed to load ad sets")}
      </p>
    )
  }
  const visible = (adSets ?? []).filter(
    a => !HIDDEN_STATUSES.has(a.effective_status ?? a.status),
  )
  if (!visible.length) {
    return <p className="text-sm text-neutral">This campaign has no ad sets.</p>
  }
  return (
    <ul className="divide-y divide-basic-blue/40">
      {visible.map(adSet => (
        <li key={adSet.id} className="py-3 space-y-2">
          <div className="flex items-center gap-2">
            <span className="text-white text-sm font-medium">{adSet.name}</span>
            <StatusBadge status={adSet.effective_status ?? adSet.status} />
          </div>
          <BudgetEditor kind="adset" entity={adSet} />
        </li>
      ))}
    </ul>
  )
}

function EditBudgetDialog({
  campaign,
  onClose,
}: {
  campaign: Campaign | null
  onClose: () => void
}) {
  if (!campaign) return null
  const isCampaignBudget = ownBudget(campaign) !== null
  return (
    <Dialog
      isOpen
      handleClose={onClose}
      width={720}
      closeOnClickOutside
      classes={{
        container: "bg-basic-black border border-basic-blue rounded-lg",
      }}
    >
      <div className="p-6 space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h3 className="text-white text-lg font-medium">Edit budget</h3>
            <p className="text-sm text-neutral mt-1">
              {campaign.name} · ID {campaign.id}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-neutral hover:text-white p-1 rounded"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-xs text-neutral">
          {isCampaignBudget
            ? "This campaign uses a campaign budget (Advantage+ campaign budget)."
            : "This campaign's budget is set on each ad set."}{" "}
          Saving updates the budget on Meta immediately.
        </p>
        {isCampaignBudget ? (
          <BudgetEditor kind="campaign" entity={campaign} />
        ) : (
          <AdSetBudgets campaign={campaign} />
        )}
      </div>
    </Dialog>
  )
}

function CampaignsTable({ adAccountId }: { adAccountId: string | null }) {
  const [search, setSearch] = useState("")
  const [editing, setEditing] = useState<Campaign | null>(null)
  const {
    data: campaigns,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useGetCampaignsQuery({ platform: "meta", adAccountId })

  // Keep the dialog showing the refetched campaign after a save.
  const editingCampaign =
    (editing && campaigns?.find(c => c.id === editing.id)) ?? editing

  const rows = useMemo(() => {
    const term = search.trim().toLowerCase()
    return (campaigns ?? [])
      .filter(c => !HIDDEN_STATUSES.has(c.effective_status ?? c.status))
      .filter(
        c =>
          !term || c.name.toLowerCase().includes(term) || c.id.includes(term),
      )
      .sort((a, b) => {
        const aActive = (a.effective_status ?? a.status) === "ACTIVE" ? 0 : 1
        const bActive = (b.effective_status ?? b.status) === "ACTIVE" ? 0 : 1
        return aActive - bActive || a.name.localeCompare(b.name)
      })
  }, [campaigns, search])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <span className="loading loading-spinner loading-lg text-primary" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="border border-red-800 rounded-md p-4 text-red-300 text-sm">
        {getServerErrorMessage(error, "Failed to load campaigns from Meta.")}
      </div>
    )
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <input
          type="search"
          placeholder="Search campaigns"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="input input-sm input-bordered w-72 bg-basic-black text-white"
        />
        <button
          type="button"
          className="btn btn-sm btn-outline"
          onClick={() => void refetch()}
          disabled={isFetching}
        >
          {isFetching ? "Refreshing…" : "Refresh from Meta"}
        </button>
      </div>

      {rows.length === 0 ? (
        <p className="text-neutral text-sm py-8 text-center">
          No campaigns found in this ad account.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-neutral border-b border-basic-blue">
                <th className="py-2 pr-4 font-medium">Campaign</th>
                <th className="py-2 pr-4 font-medium">Status</th>
                <th className="py-2 pr-4 font-medium">Budget level</th>
                <th className="py-2 pr-4 font-medium">Budget</th>
                <th className="py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {rows.map(campaign => {
                const hasCampaignBudget = ownBudget(campaign) !== null
                return (
                  <tr
                    key={campaign.id}
                    className="border-b border-basic-blue/40 hover:bg-white/5"
                  >
                    <td className="py-3 pr-4">
                      <div className="text-white font-medium">
                        {campaign.name}
                      </div>
                      <div className="text-xs text-neutral">
                        ID {campaign.id}
                      </div>
                    </td>
                    <td className="py-3 pr-4">
                      <StatusBadge
                        status={campaign.effective_status ?? campaign.status}
                      />
                    </td>
                    <td className="py-3 pr-4 text-neutral">
                      {hasCampaignBudget ? "Campaign" : "Ad set"}
                    </td>
                    <td className="py-3 pr-4 text-white">
                      {hasCampaignBudget
                        ? describeBudget(campaign)
                        : "Set per ad set"}
                    </td>
                    <td className="py-3 text-right">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        onClick={() => setEditing(campaign)}
                      >
                        Edit budget
                      </button>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <EditBudgetDialog
        campaign={editingCampaign}
        onClose={() => setEditing(null)}
      />
    </>
  )
}

/**
 * Meta Budgets: the ad account's campaigns read live from the Meta Marketing
 * API, with campaign (CBO) or ad set (ABO) budgets editable in place. No Cube
 * dependency, so it works for an account whose data has not synced yet.
 * Behind the `meta-budget-editor` flag.
 */
export default function MetaBudgetsPage() {
  const [adAccountId, setAdAccountId] = useState<string | null>(null)

  return (
    <MetaAdAccountGate
      title="Select Meta Ad Account"
      selectedAdAccountId={adAccountId}
      onSelect={setAdAccountId}
    >
      <div className="h-full px-system-md py-system-sm">
        <div className="mb-system-lg">
          <h1 className="text-white text-2xl font-medium">
            Meta Campaign Budgets
          </h1>
          <p className="text-neutral text-sm mt-1">
            Campaigns are read live from your Meta ad account
            {adAccountId ? ` (${adAccountId})` : ""}. Budget changes are sent to
            Meta right away.
          </p>
        </div>
        <div className="border border-basic-blue rounded-system-sm p-system-md">
          <CampaignsTable adAccountId={adAccountId} />
        </div>
      </div>
    </MetaAdAccountGate>
  )
}
